import { Flex, ListItem, OrderedList, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaLoading, RanaModal } from "@nens/ranalib";
import { RanaErrors } from "@nens/ranalib";

import { RanaFileIcon } from "@/lib/rana-file-data-type";
import { cancelButtonText } from "@/translations";
import { ProjectFileOrDirectory } from "@/types/api-model-simple-names";

import { useDeleteProjectFileOrDirectory } from "../use-delete-project-file-or-directory";

interface ProjectFileOrDirectoryDeleteModalProps {
  items: ProjectFileOrDirectory[];
  onClose: () => void;
}

export function ProjectFileOrDirectoryDeleteModal({
  items,
  onClose,
}: ProjectFileOrDirectoryDeleteModalProps) {
  const intl = useIntl();
  const { isMutating, errors, mutate } = useDeleteProjectFileOrDirectory(items);
  const deleteFilesOrDirectories = () => mutate(undefined, { onSuccess: onClose });

  const title = intl.formatMessage({
    id: "header.delete_file_directory",
    defaultMessage: "Delete files / folders",
  });

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={title}
      maxW="xl"
      h="fit"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: onClose,
        },
        {
          children: title,
          variant: "danger",
          isDisabled: isMutating,
          onClick: deleteFilesOrDirectories,
        },
      ]}
    >
      <Flex flexDir="column" gap={2}>
        <Text>
          <FormattedMessage
            id="message.do_you_want_to_delete"
            defaultMessage="Do you really want to delete the below files / directories?"
          />
        </Text>
        <OrderedList>
          {items.map((item) => (
            <ListItem key={item.id}>
              <Flex alignItems="center">
                <RanaFileIcon fileOrDirectory={item} />
                {item.id}
              </Flex>
            </ListItem>
          ))}
        </OrderedList>
        {isMutating && <RanaLoading size="md" isCentered />}
        <RanaErrors errors={errors} />
      </Flex>
    </RanaModal>
  );
}
